import { skapi } from "@/main";

/**
 * Written to the user's misc.signup_origin when the account is created
 * through www.bunnyquery.com. www.skapi.com never writes this key, so an
 * account whose misc has no signup_origin is assumed to have joined via skapi.
 */
export const SIGNUP_ORIGIN = "bunnyquery";

/** misc payload for skapi.signup(): stamps the new account's origin at creation. */
export function signupMisc(): string {
    return JSON.stringify({ signup_origin: SIGNUP_ORIGIN });
}

/**
 * True when the profile's approval stamp ("{approver}:approved:{timestamp}")
 * is younger than maxAgeMs. False when the stamp is missing or unparseable.
 * Used to make sure a first-login origin tag only lands on a freshly created
 * account, never on an old account that just happens to visit this site.
 */
export function accountApprovedWithin(
    user: { approved?: string } | null | undefined,
    maxAgeMs: number,
): boolean {
    const stamp = typeof user?.approved === "string" ? user.approved.split(":").pop() : "";
    let ts = Number(stamp);
    if (!Number.isFinite(ts) || ts <= 0) return false;
    if (ts < 1e12) ts *= 1000; // seconds to milliseconds
    return Date.now() - ts < maxAgeMs;
}

// ---- Which sites an account uses ---------------------------------------------
// One account, one user pool, two sites. misc.signup_origin says where the account
// was CREATED (bunnyquery.com writes "bunnyquery"; no key means skapi.com, and so
// does every account older than the key). misc.touched says where else it has
// signed in since: one entry per site, appended the first time the account signs
// in on a site that is not its home site, never for the home site itself.
//
//   signup_origin "bunnyquery", touched []            BunnyQuery only
//   signup_origin "bunnyquery", touched [skapi]       both, came from BunnyQuery
//   no signup_origin,           touched [bunnyquery]  both, came from skapi.com
//   no signup_origin,           touched []            skapi.com only
//
// The DevOps dashboard reads both keys; the user stream counts the day an entry
// appears. Analytics-grade, like signup_origin: misc is the account's own data.
export type SiteName = "skapi" | "bunnyquery";
/** This site. */
export const SITE: SiteName = "bunnyquery";
export type TouchedEntry = { site: SiteName; at: number };

/** Where the account was created, read off its misc. */
export function homeSiteOf(misc: any): SiteName {
    return misc && misc.signup_origin === "bunnyquery" ? "bunnyquery" : "skapi";
}

/** The sites an account has signed in on besides its home site, as stored. */
export function touchedSites(misc: any): TouchedEntry[] {
    const list = misc && Array.isArray(misc.touched) ? misc.touched : [];
    return list.filter((t: any) => t && typeof t.site === "string" && typeof t.at === "number");
}

/**
 * Record that the account signed in on `site`, once. Resolves true when this call
 * added the entry (the account's first sign-in here), false when it was already
 * there. The append happens against the profile as it is at write time, inside the
 * same queue every misc write goes through, so an entry the other site added a
 * moment earlier is never dropped.
 */
export function touchSite(site: SiteName = SITE): Promise<boolean> {
    return runMiscUpdate((misc) => {
        if (touchedSites(misc).some((t) => t.site === site)) return false;
        if (misc.touched !== undefined && !Array.isArray(misc.touched)) {
            // another writer's value of a different shape: not ours to replace
            throw new Error("misc.touched is not a list; it was left untouched");
        }
        // every stored entry stays as it is, whatever its shape; ours is added after
        misc.touched = [...(Array.isArray(misc.touched) ? misc.touched : []), { site, at: Date.now() }];
        return true;
    });
}

// Every misc write: one at a time, against the profile read at that moment, and
// only when `mutate` says it changed something. miscUpdater and touchSite are the
// two shapes of it.
let processing: Promise<boolean> | null = null;
async function runMiscUpdate(mutate: (misc: Record<string, any>) => boolean): Promise<boolean> {
    if (processing) {
        try { await processing; } catch { /* the earlier write reported its own failure */ }
    }

    processing = (async () => {
        // A FRESH read, not the SDK's cached copy from sign-in: the other site may have
        // written misc since this tab signed in (its "touched" entry, say), and a write
        // built on the stale copy would drop it. A refresh is one call, and a misc write
        // is a once-per-account event, so it is cheap. The refreshed user does not
        // re-run the login callback: main.ts skips it for the same user.
        const getProfile = await skapi.getProfile({ refreshToken: true });

        if (!getProfile) {
            // 프로필이 없는 경우 (로그인 안됨 등)
            throw new Error("User profile not available");
        }

        // THE RULE FOR EVERY MISC WRITE: add or change keys, never wipe what is there.
        // The stored object is read back whole and written back whole with the change
        // applied. Stored content this code cannot read (not JSON, or not an object) is
        // left exactly as it is and the write is refused: writing `{}` plus the new key
        // over it would erase whatever another writer put there.
        let currentMisc: Record<string, any> = {};
        if (getProfile.misc && typeof getProfile.misc === "string") {
            let parsed: any;
            try {
                parsed = JSON.parse(getProfile.misc);
            } catch {
                throw new Error("misc is not JSON; it was left untouched rather than overwritten");
            }
            if (parsed !== null && (typeof parsed !== "object" || Array.isArray(parsed))) {
                throw new Error("misc is not an object; it was left untouched rather than overwritten");
            }
            currentMisc = parsed === null ? {} : parsed; // a stored JSON null holds nothing to keep
        } else if (getProfile.misc && typeof getProfile.misc === "object" && !Array.isArray(getProfile.misc)) {
            currentMisc = { ...(getProfile.misc as Record<string, any>) };
        }

        if (!mutate(currentMisc)) return false;

        await skapi.updateProfile({
            misc: JSON.stringify(currentMisc),
        });
        return true;
    })().finally(() => {
        processing = null;
    });

    return processing;
}

export async function miscUpdater(toUpdate: any): Promise<void> {
    const nextUpdate = { ...toUpdate };
    await runMiscUpdate((currentMisc) => {
        for (let key in nextUpdate) {
            if (nextUpdate[key] === null) {
                delete currentMisc[key];
                continue;
            }

            currentMisc[key] = nextUpdate[key];
        }
        return true;
    });
}
